import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, Users, Award, ArrowRight, Play, CheckCircle2, Calendar, Shield, Heart, Target, Sparkles, Bot, Code, Puzzle, Palette, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import EditableFaqList from '../components/EditableFaqList';
import { mockWorkshops, mockFAQ, mockRobotProjects } from '../data/mock';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import RobotGallery from '../components/RobotGallery';
import Reveal from '../components/Reveal';
import CircuitDivider from '../components/CircuitDivider';
import IconBadge from '../components/IconBadge';
import SectionHeader from '../components/SectionHeader';
import StepCards from '../components/StepCards';
import FeatureCard from '../components/FeatureCard';
import TestimonialsSection from '../components/TestimonialsSection';
import CtaBanner from '../components/CtaBanner';
import { useTestimonials } from '../hooks/useTestimonials';
import { useBrandName } from '../context/SettingsContext';
import EditableText from '../components/EditableText';
import { BACKEND_URL, BTN_PRIMARY, BTN_OUTLINE } from '../config/brand';
import PlanningSlots from '../components/PlanningSlots';
import { IMAGES, WORKSHOP_IMAGES } from '../config/images';

const LEVEL_BY_TYPE = {
  decouverte: 'Découverte',
  defi: 'Ludique',
  particulier: 'Personnalisé',
};

const mapApiWorkshop = (w) => ({
  id: w.id,
  slug: w.type,
  title: w.name,
  ageRange: w.age_range || '6-16 ans',
  duration: w.duration,
  price: w.price ?? 0,
  isFree: (w.price ?? 0) === 0,
  description: w.description,
  image: w.image_url || WORKSHOP_IMAGES[w.type] || WORKSHOP_IMAGES.decouverte,
  level: LEVEL_BY_TYPE[w.type] || w.type,
  capacity: w.max_capacity ?? 10,
});

const WORKSHOP_TO_OFFER_SLUG = {
  decouverte: 'atelier-decouverte',
  defi: 'atelier-defi',
  particulier: 'atelier-particulier',
};

const ATELIER_STEPS = [
  { icon: Calendar, number: 1, title: 'Réservez en ligne', description: 'Choisissez votre centre et votre créneau parmi nos disponibilités' },
  { icon: Heart, number: 2, title: 'Votre enfant découvre', description: 'Il apprend en s\'amusant avec nos formateurs passionnés' },
  { icon: Target, number: 3, title: 'Il progresse', description: 'Vous voyez ses compétences se développer semaine après semaine' },
];

const PARENT_BENEFITS = [
  { icon: CheckCircle2, variant: 'purple', title: 'Premier atelier OFFERT', description: 'Testez sans risque. Votre enfant découvre gratuitement avant tout engagement.' },
  { icon: Users, variant: 'orange', title: 'Petits groupes de 8-10', description: 'Suivi personnalisé avec des formateurs passionnés et qualifiés.' },
  { icon: Award, variant: 'purple', title: "5 centres sur la Côte d'Azur", description: 'Nice, Cannes, Monaco, Pégomas, Cagnes-sur-Mer. Proche de chez vous.' },
];

const LEARNING_SKILLS = [
  { icon: Bot, variant: 'purple', title: 'Robotique', description: 'Construction et programmation de robots' },
  { icon: Code, variant: 'orange', title: 'Programmation', description: 'Scratch, Python, Arduino' },
  { icon: Puzzle, variant: 'purple', title: 'Logique', description: 'Résolution de problèmes complexes' },
  { icon: Palette, variant: 'orange', title: 'Créativité', description: 'Projets personnalisés et innovants' },
];

const Ateliers = () => {
  const brandName = useBrandName();
  const [workshops, setWorkshops] = useState(mockWorkshops);
  const [atelierSlots, setAtelierSlots] = useState([]);
  const [atelierOffers, setAtelierOffers] = useState([]);
  const testimonials = useTestimonials(3);

  useEffect(() => {
    if (!BACKEND_URL) return;
    const loadWorkshops = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/workshops`);
        if (!res.ok) return;
        const data = await res.json();
        const ateliers = (Array.isArray(data) ? data : [])
          .filter((w) => w.type !== 'cursus')
          .map(mapApiWorkshop);
        if (ateliers.length > 0) {
          setWorkshops(ateliers);
        }
      } catch {
        // fallback mock
      }
    };
    loadWorkshops();
  }, []);

  useEffect(() => {
    if (!BACKEND_URL) return;
    const loadSlots = async () => {
      try {
        const [offersRes, schedRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/offers`),
          fetch(`${BACKEND_URL}/api/schedules`),
        ]);
        if (!offersRes.ok || !schedRes.ok) return;
        const offers = await offersRes.json();
        const schedules = await schedRes.json();
        const atelierOffersList = Array.isArray(offers) ? offers : [];
        const atelierIds = new Set(
          atelierOffersList.filter((o) => o.category === "atelier").map((o) => o.id)
        );
        const rdvOfferIds = new Set(
          atelierOffersList.filter((o) => o.slug === "atelier-particulier").map((o) => o.id)
        );
        setAtelierOffers(atelierOffersList);
        setAtelierSlots(
          (Array.isArray(schedules) ? schedules : []).filter(
            (s) =>
              !s.closed &&
              !rdvOfferIds.has(s.offer_id) &&
              (atelierIds.has(s.offer_id) || (!s.offer_id && s.workshop_id))
          )
        );
      } catch {
        setAtelierSlots([]);
      }
    };
    loadSlots();
  }, []);

  const reservationLink = (workshop) => {
    const offerSlug = WORKSHOP_TO_OFFER_SLUG[workshop.slug] || workshop.slug;
    if (offerSlug) {
      return `/reserver?type=atelier&offer=${offerSlug}`;
    }
    return `/reserver?type=atelier&id=${workshop.id}`;
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section - Landing Page Style */}
      <section className="relative pt-below-site-header pb-20 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[rgba(58,0,224,0.06)] via-white to-[rgba(250,176,58,0.08)]"></div>
        <div className="absolute top-20 left-10 w-72 h-72 bg-[rgba(58,0,224,0.1)] rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 right-10 w-64 h-64 bg-[rgba(250,176,58,0.14)] rounded-full blur-3xl"></div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <Reveal animation="fade-up">
            <div>
              <p className="text-[#3a00e0] text-sm font-semibold uppercase tracking-wider mb-4 inline-flex items-center gap-1.5">
                <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
                <EditableText
                  editKey="ateliers.hero.badge"
                  defaultValue="Premier atelier offert"
                  as="span"
                  className="text-[#3a00e0] text-sm font-semibold uppercase tracking-wider"
                />
              </p>
              
              <EditableText
                editKey="ateliers.hero.title"
                defaultValue="Offrez à votre enfant les compétences du futur"
                as="h1"
                className="text-5xl lg:text-6xl font-bold text-slate-900 mb-6 leading-tight tracking-tight"
              />
              
              <EditableText
                editKey="ateliers.hero.subtitle"
                defaultValue="Des ateliers robotique et programmation sur mesure pour les 6-16 ans. Développez la créativité, la logique et la confiance de votre enfant."
                as="p"
                multiline
                className="text-xl text-slate-600 mb-8"
              />
              
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Link to="/reserver?type=atelier&offer=atelier-decouverte">
                  <Button size="lg" className={`${BTN_PRIMARY} text-lg px-8 py-6`}>
                    <EditableText editKey="ateliers.hero.cta" defaultValue="Réserver l'atelier GRATUIT" className="inline" />
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button size="lg" variant="outline" className={`${BTN_OUTLINE} text-lg px-8 py-6`}>
                    <EditableText editKey="ateliers.hero.ctaSecondary" defaultValue="Nous contacter" className="inline" />
                  </Button>
                </Link>
              </div>

              <div className="grid grid-cols-3 gap-6">
                {[
                  { value: '500+', label: 'Enfants formés', key: 'ateliers.hero.stat.1.label' },
                  { value: '4.9/5', label: 'Satisfaction parents', key: 'ateliers.hero.stat.2.label' },
                  { value: '5', label: 'Centres', key: 'ateliers.hero.stat.3.label' },
                ].map((stat) => (
                  <div key={stat.key} className="text-center">
                    <div className="text-3xl font-bold text-gradient-brand mb-1">{stat.value}</div>
                    <EditableText
                      editKey={stat.key}
                      defaultValue={stat.label}
                      as="div"
                      className="text-sm text-slate-600"
                    />
                  </div>
                ))}
              </div>
            </div>
            </Reveal>
            
            <Reveal animation="fade-in" delay={120} className="relative">
              <div className="aspect-square rounded-2xl overflow-hidden border-2 border-[rgba(58,0,224,0.18)] shadow-2xl shadow-[0_20px_40px_-20px_rgba(58,0,224,0.25)]">
                <img 
                  src={IMAGES.workshops.hero} 
                  alt={`Atelier ${brandName}`}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 bg-white/95 backdrop-blur-sm border border-slate-200 rounded-xl p-4 shadow-xl">
                <div className="flex items-center gap-2">
                  <IconBadge icon={Shield} variant="orange" size="sm" />
                  <EditableText
                    editKey="ateliers.hero.floating_badge"
                    defaultValue="Satisfait ou remboursé"
                    as="span"
                    className="text-slate-900 font-semibold text-sm"
                  />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <CircuitDivider />

      {/* Video Section */}
      <section className="py-20 px-4 bg-slate-50">
        <div className="max-w-5xl mx-auto">
          <SectionHeader
            eyebrow="Vidéo"
            eyebrowKey="ateliers.video.eyebrow"
            title={`Découvrez un atelier ${brandName}`}
            titleKey="ateliers.video.title"
            subtitle="Immergez-vous dans l'univers passionnant de nos ateliers"
            subtitleKey="ateliers.video.subtitle"
            reveal
          />
          
          <Reveal animation="scale-in">
          <div className="aspect-video rounded-2xl overflow-hidden border-2 border-[rgba(58,0,224,0.18)] shadow-2xl relative group cursor-pointer card-hover-glow">
            <img 
              src={IMAGES.workshops.videoPreview} 
              alt="Vidéo atelier"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/35 group-hover:bg-black/45 transition-all flex items-center justify-center">
              <div className="w-24 h-24 bg-gradient-to-r from-[#3a00e0] to-[#fab03a] rounded-full flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg shadow-[0_12px_30px_-10px_rgba(58,0,224,0.45)]">
                <Play className="h-12 w-12 text-white ml-2" aria-hidden="true" />
              </div>
            </div>
            <div className="absolute bottom-6 left-6 bg-white/95 backdrop-blur-sm rounded-lg px-4 py-2 flex items-center gap-2 shadow-md border border-slate-200">
              <Play className="h-4 w-4 text-[#3a00e0]" aria-hidden="true" />
              <EditableText
                editKey="ateliers.video.overlay_label"
                defaultValue="Regarder la vidéo (2 min)"
                as="p"
                className="text-slate-900 font-semibold text-sm"
              />
            </div>
          </div>
          </Reveal>
        </div>
      </section>

      <CircuitDivider className="my-0" />

      {/* Nos 3 Formules */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <SectionHeader
            eyebrow="Formules"
            eyebrowKey="ateliers.formulas.eyebrow"
            title="Nos formules d'ateliers"
            titleKey="ateliers.formulas.title"
            subtitle="Choisissez la formule adaptée aux besoins de votre enfant"
            subtitleKey="ateliers.formulas.subtitle"
            reveal
          />

          <div className="grid lg:grid-cols-3 gap-8">
            {workshops.map((workshop, index) => (
              <Reveal key={workshop.id} animation="fade-up" delay={index * 100}>
              <Card
                className={`relative overflow-hidden card-hover-glow h-full ${
                  workshop.isFree
                    ? 'bg-gradient-to-br from-green-500/5 to-emerald-600/5 border-green-500/30 ring-1 ring-green-500/20'
                    : 'bg-white border-slate-200 shadow-sm hover:border-[rgba(58,0,224,0.28)]'
                }`}
              >
                {workshop.isFree && (
                  <div className="absolute top-4 right-4 z-10">
                    <Badge className="bg-green-500 text-white border-0 shadow-lg">
                      <EditableText editKey="ateliers.formulas.free_badge" defaultValue="OFFERT" as="span" />
                    </Badge>
                  </div>
                )}

                <div className="h-48 overflow-hidden">
                  <img
                    src={workshop.image}
                    alt={workshop.title}
                    className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                  />
                </div>

                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant="outline" className="border-[rgba(58,0,224,0.25)] text-[#3a00e0] bg-[rgba(58,0,224,0.05)]">
                      {workshop.ageRange}
                    </Badge>
                    <Badge variant="outline" className="border-slate-300 text-slate-500">
                      {workshop.level}
                    </Badge>
                  </div>
                  <CardTitle className="text-slate-900 text-xl">{workshop.title}</CardTitle>
                  <CardDescription className="text-slate-600 line-clamp-2">
                    {workshop.description}
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex items-center text-slate-600 text-sm">
                      <Clock className="h-4 w-4 text-[#3a00e0] mr-2 shrink-0" />
                      <span>{workshop.duration}</span>
                    </div>
                    <div className="flex items-center text-slate-600 text-sm">
                      <Users className="h-4 w-4 text-[#3a00e0] mr-2 shrink-0" />
                      <span>Max {workshop.capacity} enfants</span>
                    </div>
                  </div>

                  <div className="flex items-baseline gap-2 pt-2 border-t border-slate-200">
                    {workshop.isFree ? (
                      <>
                        <span className="text-3xl font-bold text-green-600">GRATUIT</span>
                        <span className="text-xs text-slate-500">premier atelier</span>
                      </>
                    ) : (
                      <>
                        <span className="text-3xl font-bold text-slate-900">{workshop.price}€</span>
                        <span className="text-sm text-slate-600">/ atelier</span>
                      </>
                    )}
                  </div>

                  <Link to={reservationLink(workshop)} className="block">
                    <Button className={`w-full ${BTN_PRIMARY}`}>
                      <EditableText
                        editKey={
                          workshop.slug === 'particulier'
                            ? 'ateliers.card.cta_rdv'
                            : workshop.isFree
                              ? 'ateliers.card.cta_free'
                              : 'ateliers.card.cta'
                        }
                        defaultValue={
                          workshop.slug === 'particulier'
                            ? 'Demander un rendez-vous'
                            : workshop.isFree
                              ? 'Réserver gratuitement'
                              : 'Réserver'
                        }
                        as="span"
                      />
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Comment ça marche */}
      <section className="py-20 px-4 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <SectionHeader
            eyebrow="Simple et efficace"
            eyebrowKey="ateliers.how.eyebrow"
            title="Comment ça marche ?"
            titleKey="ateliers.how.title"
            subtitle="En 3 étapes simples, votre enfant commence l'aventure"
            subtitleKey="ateliers.how.subtitle"
            reveal
          />
          <StepCards steps={ATELIER_STEPS} columns={3} keyPrefix="ateliers.how" />
        </div>
      </section>

      {/* Calendrier/Disponibilités */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <SectionHeader
            eyebrow="Planning"
            eyebrowKey="ateliers.planning.eyebrow"
            title="Quand et où s'inscrire ?"
            titleKey="ateliers.planning.title"
            subtitle="Choisissez le centre et l'horaire qui vous conviennent"
            subtitleKey="ateliers.planning.subtitle"
            reveal
          />

          <div className="mb-8">
            <PlanningSlots
              slots={atelierSlots}
              emptyText="Les créneaux ateliers seront bientôt disponibles. Contactez-nous pour plus d'informations."
              emptyKey="ateliers.planning.empty"
              getReserveHref={(slot) => {
                const offer = atelierOffers.find((o) => o.id === slot.offer_id);
                const params = new URLSearchParams({ type: "atelier" });
                if (offer?.slug) params.set("offer", offer.slug);
                if (slot.formula_id) params.set("formula", slot.formula_id);
                if (slot.center_id) params.set("center", slot.center_id);
                if (slot.id) params.set("schedule", slot.id);
                return `/reserver?${params.toString()}`;
              }}
            />
          </div>

          <Card className="bg-[rgba(250,176,58,0.12)] border-[rgba(250,176,58,0.4)] card-hover-glow">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <IconBadge icon={Zap} variant="orange" size="sm" className="shrink-0" />
                <div>
                  <EditableText
                    editKey="ateliers.planning.alert.title"
                    defaultValue="Places limitées !"
                    as="h3"
                    className="text-slate-900 font-semibold mb-2"
                  />
                  <EditableText
                    editKey="ateliers.planning.alert.text"
                    defaultValue="Plus que 12 places disponibles ce mois-ci. Les créneaux se remplissent rapidement. Réservez maintenant pour garantir votre place !"
                    as="p"
                    multiline
                    className="text-slate-700 text-sm"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Ce que votre enfant va apprendre */}
      <section className="py-20 px-4 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <SectionHeader
            eyebrow="Compétences"
            eyebrowKey="ateliers.skills.eyebrow"
            title="Ce que votre enfant va apprendre"
            titleKey="ateliers.skills.title"
            subtitle="Des compétences concrètes pour son avenir"
            subtitleKey="ateliers.skills.subtitle"
            reveal
          />

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {LEARNING_SKILLS.map((skill, i) => (
              <Reveal key={skill.title} animation="fade-up" delay={i * 60}>
                <FeatureCard
                  {...skill}
                  size="lg"
                  titleKey={`ateliers.skills.${i + 1}.title`}
                  descriptionKey={`ateliers.skills.${i + 1}.text`}
                />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Pourquoi les parents choisissent DigiRobot */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <SectionHeader
            eyebrow="Nos atouts"
            eyebrowKey="ateliers.why.eyebrow"
            title={`Pourquoi les parents choisissent ${brandName}`}
            titleKey="ateliers.why.title"
            reveal
          />

          <div className="grid md:grid-cols-3 gap-6">
            {PARENT_BENEFITS.map((item, i) => (
              <Reveal key={item.title} animation="fade-up" delay={i * 80}>
                <FeatureCard
                  {...item}
                  centered={false}
                  titleKey={`ateliers.why.${i + 1}.title`}
                  descriptionKey={`ateliers.why.${i + 1}.text`}
                />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <TestimonialsSection
        testimonials={testimonials}
        eyebrowKey="ateliers.testimonials.eyebrow"
        titleKey="ateliers.testimonials.title"
        ratingKey="ateliers.testimonials.rating"
      />

      {/* FAQ */}
      <section className="py-20 px-4 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <SectionHeader
            eyebrow="FAQ"
            eyebrowKey="ateliers.faq.eyebrow"
            title="Questions fréquentes"
            titleKey="ateliers.faq.title"
            subtitle="Tout ce que vous devez savoir avant de réserver"
            subtitleKey="ateliers.faq.subtitle"
            reveal
          />

          <EditableFaqList items={mockFAQ} keyPrefix="ateliers.faq" />
        </div>
      </section>

      <RobotGallery
        projects={mockRobotProjects}
        limit={16}
        showExpand
        expandStep={16}
        title="50+ projets réalisables en atelier"
        titleKey="ateliers.gallery.title"
        subtitle="Chaque séance, les enfants construisent et programment un robot différent — du bulldozer au robot sumo, en passant par le lanceur d'avions."
        subtitleKey="ateliers.gallery.subtitle"
      />

      <CtaBanner
        title="Prêt à offrir le meilleur à votre enfant ?"
        titleKey="ateliers.cta.title"
        subtitle="Réservez maintenant votre atelier découverte GRATUIT et voyez la différence !"
        subtitleKey="ateliers.cta.subtitle"
        footer="Sans engagement · Annulation gratuite 48h avant · Satisfait ou remboursé"
        footerKey="ateliers.cta.footer"
      >
        <Link to="/reserver?type=atelier&offer=atelier-decouverte">
          <Button size="lg" className="bg-white text-[#3a00e0] hover:bg-[rgba(58,0,224,0.06)] text-xl px-12 py-8">
            <EditableText editKey="ateliers.cta.button" defaultValue="Réserver l'atelier GRATUIT" className="inline" />
            <ArrowRight className="ml-2 h-6 w-6" />
          </Button>
        </Link>
      </CtaBanner>

      <Footer />
    </div>
  );
};

export default Ateliers;
